import { cva, type VariantProps } from 'class-variance-authority';
import { Slot } from 'radix-ui';
import type * as React from 'react';

import { cn } from '@/lib/utils';

/**
 * A badge labels a state. It is never a button and never a filled block of
 * colour: a saturated pill repeated down a list is the fastest way to make a
 * calm screen shout.
 *
 * So every variant is a tinted surface with a matching border and text at the
 * darker end of the same ramp — legible at 11px, and quiet enough that twenty
 * of them on a board still leave the accent free to mean something.
 *
 * The radius is `sm`, not full. A pill is for a count; this carries a word,
 * and a softly-rounded rectangle sits better beside a line of text.
 */
const badgeVariants = cva(
  [
    'type-caption inline-flex w-fit shrink-0 items-center justify-center gap-1 whitespace-nowrap',
    'rounded-sm border px-1.5 py-0.5 font-medium',
    "[&>svg]:pointer-events-none [&>svg]:size-3 [&>svg:not([class*='size-'])]:size-3",
  ],
  {
    variants: {
      variant: {
        neutral: 'border-border bg-surface-muted text-foreground-secondary',
        accent: 'border-accent-subtle-border bg-accent-subtle text-accent-text',
        success: 'border-success-subtle-border bg-success-subtle text-success-text',
        warning: 'border-warning-subtle-border bg-warning-subtle text-warning-text',
        destructive: 'border-destructive-subtle-border bg-destructive-subtle text-destructive-text',
        /** Border only — for a count or a qualifier that should barely register. */
        outline: 'border-border bg-transparent text-foreground-muted',
      },
    },
    defaultVariants: {
      variant: 'neutral',
    },
  }
);

function Badge({
  className,
  variant,
  asChild = false,
  ...props
}: React.ComponentProps<'span'> & VariantProps<typeof badgeVariants> & { asChild?: boolean }) {
  const Comp = asChild ? Slot.Root : 'span';

  return (
    <Comp
      data-slot="badge"
      data-variant={variant ?? 'neutral'}
      className={cn(badgeVariants({ variant }), className)}
      {...props}
    />
  );
}

export { Badge, badgeVariants };
